import React, { useMemo, useState } from 'react';
import type { EChartsCoreOption } from 'echarts/core';
import ChartComponent from 'src/helpers/Echarts/ChartComponent';
import { extractData, prepare24HrsChartData } from './modules';
import { graphic } from 'echarts';

const apiRespoData: any = {
    zoneA: {
        name: 'ToD Zone A',
        beforeMYT: {
            hours: 8,
            timeslot: '12AM-6AM, 10PM-12AM',
            units: 1334100,
            rebateRate: '₹-1.5/unit',
            rebateAmount: -2001150,
        },
        afterMYT: {
            hours: 6,
            timeslot: '12AM-6AM',
            units: 1000575,
            rebateRate: null,
            rebateAmount: 0,
        },
    },
    zoneB: {
        name: 'ToD Zone B',
        beforeMYT: {
            hours: 9,
            timeslot: '12PM-6PM',
            units: 1489301,
            rebateRate: 0,
            rebateAmount: 0,
        },
        afterMYT: {
            hours: 3,
            timeslot: '6AM-9AM',
            units: 496434,
            rebateRate: null,
            rebateAmount: 0,
        },
    },
    zoneC: {
        name: 'ToD Zone C',
        beforeMYT: {
            hours: 3,
            timeslot: '9AM-12PM',
            units: 503250,
            rebateRate: '₹0.8/unit',
            rebateAmount: 402600,
        },
        afterMYT: {
            hours: 8,
            timeslot: '9AM-5PM',
            units: 1342000,
            rebateRate: '₹-1.3/unit (-15%)',
            rebateAmount: -1744600,
        },
    },
    zoneD: {
        name: 'ToD Zone D',
        beforeMYT: {
            hours: 4,
            timeslot: '6PM-10PM',
            units: 683900,
            rebateRate: '₹1.1/unit',
            rebateAmount: 752290.0000000001,
        },
        afterMYT: {
            hours: 7,
            timeslot: '5PM-12AM',
            units: 1196825,
            rebateRate: '₹2.17/unit (+25%)',
            rebateAmount: 2597110.25,
        },
    },
};


type props = {
    apiRespoData: any
}

const EpiechartExample = ({ apiRespoData }: props) => {

    const data = useMemo(() => extractData(apiRespoData), [extractData, apiRespoData])

    const options: EChartsCoreOption = useMemo(
        () => ({
            tooltip: {
                trigger: 'item',
                formatter: (params: any) => {
                    const { seriesName, name, value, percent } = params;
                    return `
        <div style="background: #fff; padding: 10px; border: 1px solid #ccc; border-radius: 5px; box-shadow: 0 2px 5px rgba(0,0,0,0.2);">
          <h3 style="margin: 0 0 10px; font-size: 16px; color: #333;">${seriesName}</h3>
          <table style="border-collapse: collapse; font-size: 14px;">
            <tr style="background: #f5f5f5;">
              <th style="padding: 5px; border: 1px solid #ddd;">Category</th>
              <th style="padding: 5px; border: 1px solid #ddd;">Value</th>
              <th style="padding: 5px; border: 1px solid #ddd;">Percent</th>
            </tr>
            <tr>
              <td style="padding: 5px; border: 1px solid #ddd;">Zone ${name}</td>
              <td style="padding: 5px; border: 1px solid #ddd;">${value / 4.16} hrs</td>
              <td style="padding: 5px; border: 1px solid #ddd;">${percent}%</td>
            </tr>
          </table>
        </div>
      `;
                }
            },
            legend: {
                bottom: 10,
                left: 'center',
                data: ['Category A', 'Category B', 'Category C', 'Subcategory A1', 'Subcategory A2', 'Subcategory B1', 'Subcategory B2']
            },
            series: [
                {
                    type: 'gauge',
                    center: ['50%', '50%'],
                    radius: '60%',
                    startAngle: 90,
                    endAngle: -269.9,
                    min: 0,
                    max: 24,
                    splitNumber: 24,
                    itemStyle: {
                        color: '#FFAB91'
                    },
                    pointer: {
                        show: false
                    },
                    axisLine: {
                        lineStyle: {
                            width: 30
                        }
                    },
                    axisTick: {
                        distance: -40,
                        splitNumber: 5,
                        lineStyle: {
                            width: 2,
                            color: '#999'
                        }
                    },
                    splitLine: {
                        distance: -52,
                        length: 14,
                        lineStyle: {
                            width: 3,
                            color: '#999'
                        }
                    },
                    axisLabel: {
                        distance: -5,
                        color: '#999',
                        fontSize: 14,
                        formatter: function (value: any) {
                            return value === 24 ? '' : value;
                        }
                    },
                    anchor: {
                        show: false
                    },
                    title: {
                        show: false
                    },
                },
                {
                    name: 'Outer Ring',
                    type: 'pie',

                    radius: ['45%', '60%'],
                    avoidLabelOverlap: true,

                    label: {
                        show: true,
                        position: 'inside',
                        formatter: '{b}',
                        fontSize: 12,
                        color: '#333'
                    },
                    labelLine: {
                        show: false,
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: 14,
                            fontWeight: 'bold'
                        }
                    },
                    data: data?._afterMYTData
                },
                {
                    name: 'Inner Ring',
                    type: 'pie',
                    radius: ['25%', '40%'],
                    avoidLabelOverlap: true,
                    label: {
                        show: true,
                        position: 'inside',
                        formatter: '{b}',
                        fontSize: 12,
                        color: '#333'
                    },
                    labelLine: {
                        show: false,
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: 14,
                            fontWeight: 'bold'
                        }
                    },
                    data: data?._beforeMYTData
                }
            ]
        }),
        [data]
    );

    return (

        <div className=' relative'>
            <ChartComponent options={options} style={{ height: '250px' }} />
            {/* <img src='https://i.ibb.co/tpRTFV55/cropped-circle-image-2.png' width={"60px"} height={"60px" } className='absolute top-[50%] left-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-90 '  /> */}
        </div>

    );
};

export default React.memo(EpiechartExample);